/** Shared formatting and comparison helpers for the Buildings (/buildings) page. */

const LITTLE_CHANGE_THRESHOLD_PP = 0.5;
const LITTLE_CHANGE_THRESHOLD_PCT = 0.5;

export function isMissingNumber(value: number | null | undefined): boolean {
  return value == null || !Number.isFinite(value);
}

export function formatSignedPercentChange(value: number | null | undefined, decimals = 1): string {
  if (isMissingNumber(value)) return '—';
  const sign = value! > 0 ? '+' : value! < 0 ? '' : '';
  return `${sign}${value!.toFixed(decimals)}%`;
}

export function formatPercentagePointChange(value: number | null | undefined, decimals = 1): string {
  if (isMissingNumber(value)) return '—';
  const sign = value! > 0 ? '+' : value! < 0 ? '' : '';
  return `${sign}${value!.toFixed(decimals)} percentage points`;
}

export function percentChangeLabel(baselineYear: number | null | undefined): string {
  return baselineYear != null ? `% change since ${baselineYear}` : '% change since baseline';
}

export function changeDirectionWord(
  percentChange: number | null | undefined,
  threshold = LITTLE_CHANGE_THRESHOLD_PCT
): 'increase' | 'decrease' | 'little changed' {
  if (isMissingNumber(percentChange)) return 'little changed';
  if (percentChange! > threshold) return 'increase';
  if (percentChange! < -threshold) return 'decrease';
  return 'little changed';
}

export function compareLocalToCalifornia(
  local: number | null | undefined,
  california: number | null | undefined
): { differencePercent: number | null; comparison: 'above' | 'below' | 'about the same as' } {
  if (isMissingNumber(local) || isMissingNumber(california) || california! <= 0) {
    return { differencePercent: null, comparison: 'about the same as' };
  }
  const differencePercent = ((local! - california!) / california!) * 100;
  let comparison: 'above' | 'below' | 'about the same as' = 'about the same as';
  if (differencePercent > LITTLE_CHANGE_THRESHOLD_PCT) comparison = 'above';
  else if (differencePercent < -LITTLE_CHANGE_THRESHOLD_PCT) comparison = 'below';
  return { differencePercent, comparison };
}

export function latestCommonYear(yearLists: number[][]): number | null {
  if (!yearLists.length) return null;
  const sets = yearLists.map((years) => new Set(years));
  const intersection = yearLists[0].filter((y) => sets.every((s) => s.has(y)));
  if (!intersection.length) return null;
  return Math.max(...intersection);
}

export function latestCommonPositiveYear(
  localYears: number[],
  localValues: number[],
  compareYears: number[],
  compareValues: number[]
): { year: number; local: number; compare: number } | null {
  const localMap = new Map<number, number>();
  localYears.forEach((y, i) => {
    const v = Number(localValues[i]);
    const year = Number(y);
    if (Number.isFinite(v) && v > 0 && Number.isFinite(year)) localMap.set(year, v);
  });
  const compareMap = new Map<number, number>();
  compareYears.forEach((y, i) => {
    const v = Number(compareValues[i]);
    const year = Number(y);
    if (Number.isFinite(v) && v > 0 && Number.isFinite(year)) compareMap.set(year, v);
  });
  const common = Array.from(localMap.keys()).filter((y) => compareMap.has(y));
  if (!common.length) return null;
  const year = Math.max(...common);
  return { year, local: localMap.get(year)!, compare: compareMap.get(year)! };
}

/** Latest year with a positive finite value in a single local series. */
/** Map a benchmark series onto the local chart's year labels (null = gap). */
export function alignSeriesDataToLabels(
  targetLabels: number[],
  sourceLabels: number[],
  sourceData: number[]
): (number | null)[] {
  const valueByYear = new Map<number, number>();
  sourceLabels.forEach((y, i) => {
    const year = Number(y);
    const v = sourceData[i];
    if (Number.isFinite(year) && v != null && Number.isFinite(v) && v > 0) {
      valueByYear.set(year, v);
    }
  });
  return targetLabels.map((y) => {
    const v = valueByYear.get(Number(y));
    return v != null ? v : null;
  });
}

export function mergePerCapitaCaliforniaDatasets(
  localChart: { labels: number[]; datasets: Record<string, unknown>[] },
  californiaChart?: { labels: number[]; datasets: Record<string, unknown>[] } | null
): Record<string, unknown>[] {
  const caSets = californiaChart?.datasets ?? [];
  if (!caSets.length) return localChart.datasets;
  const caLabels = (californiaChart?.labels ?? []) as number[];
  return [
    ...localChart.datasets,
    ...caSets.map((ds) => ({
      ...ds,
      data: alignSeriesDataToLabels(
        localChart.labels,
        caLabels,
        (ds.data as number[]) ?? []
      ),
    })),
  ];
}

export function latestPositiveYearFromSeries(
  years: number[],
  values: number[]
): { year: number; value: number } | null {
  for (let i = years.length - 1; i >= 0; i--) {
    const year = Number(years[i]);
    const v = Number(values[i]);
    if (Number.isFinite(year) && Number.isFinite(v) && v > 0) {
      return { year, value: v };
    }
  }
  return null;
}

export function formatCurrencyUsd(
  value: number | null | undefined,
  { decimals = 0, perUnit }: { decimals?: number; perUnit?: string } = {}
): string {
  if (isMissingNumber(value)) return '—';
  const base = value!.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return perUnit ? `$${base}${perUnit}` : `$${base}`;
}

export function formatCount(value: number | null | undefined): string {
  if (isMissingNumber(value) || value! <= 0) return '—';
  return Math.round(value!).toLocaleString();
}

export function formatMw(value: number | null | undefined, decimals = 1): string {
  if (isMissingNumber(value) || value! <= 0) return '—';
  return `${value!.toFixed(decimals)} MW`;
}

export function impliedEmissionsChangePercent(
  useChangePercent: number | null,
  intensityChangePercent: number | null
): number | null {
  if (isMissingNumber(useChangePercent) || isMissingNumber(intensityChangePercent)) return null;
  const factor = (1 + useChangePercent! / 100) * (1 + intensityChangePercent! / 100) - 1;
  return factor * 100;
}

export type ChartSeriesSlice = {
  labels: number[];
  datasets: { data?: number[] }[];
};

export function sumChartValueAtYear(chart: ChartSeriesSlice | null | undefined, year: number): number | null {
  if (!chart?.labels?.length) return null;
  const idx = chart.labels.indexOf(year);
  if (idx < 0) return null;
  const total = (chart.datasets || []).reduce((sum, ds) => {
    const v = ds.data?.[idx];
    return sum + (v != null && Number.isFinite(Number(v)) ? Number(v) : 0);
  }, 0);
  return total > 0 ? total : null;
}

export function latestPositiveYearPair(
  resChart: ChartSeriesSlice | null | undefined,
  comChart: ChartSeriesSlice | null | undefined
): {
  year: number;
  residential: number;
  commercial: number;
  total: number;
} | null {
  if (!resChart?.labels?.length || !comChart?.labels?.length) return null;
  const resLabels = resChart.labels;
  const comLabels = new Set(comChart.labels);
  const years = resLabels.filter((y) => comLabels.has(y)).sort((a, b) => a - b);
  for (let i = years.length - 1; i >= 0; i--) {
    const year = years[i];
    const res = sumChartValueAtYear(resChart, year);
    const com = sumChartValueAtYear(comChart, year);
    if (res != null && com != null) {
      return { year, residential: res, commercial: com, total: res + com };
    }
  }
  return null;
}

export function sectorSharePercent(sectorValue: number, totalValue: number): number | null {
  if (!Number.isFinite(sectorValue) || !Number.isFinite(totalValue) || totalValue <= 0) return null;
  return Math.round((sectorValue / totalValue) * 1000) / 10;
}

export function unitsByYearFromRecords<T extends { year: number }>(
  records: T[],
  getUnits: (row: T) => number | null | undefined
): Map<number, number> {
  const map = new Map<number, number>();
  for (const row of records) {
    const units = getUnits(row);
    if (units == null || !Number.isFinite(units)) continue;
    map.set(row.year, (map.get(row.year) ?? 0) + units);
  }
  return map;
}

export function latestCompletePositiveYear(unitByYear: Map<number, number>): number | null {
  const years = Array.from(unitByYear.keys())
    .filter((y) => (unitByYear.get(y) ?? 0) > 0)
    .sort((a, b) => a - b);
  return years.length ? years[years.length - 1] : null;
}

export function priorCompletePositiveYear(
  unitByYear: Map<number, number>,
  beforeYear: number
): number | null {
  const years = Array.from(unitByYear.keys())
    .filter((y) => y < beforeYear && (unitByYear.get(y) ?? 0) > 0)
    .sort((a, b) => a - b);
  return years.length ? years[years.length - 1] : null;
}

export function yoyPercentChange(current: number | null, prior: number | null): number | null {
  if (current == null || prior == null || prior <= 0) return null;
  return ((current - prior) / prior) * 100;
}
